import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Platform,
  Modal,
  Dimensions,
} from 'react-native';

import { MoreHorizontal, Trash2 } from './icons';
import { featureFlags } from '../config/featureFlags';
import { getScopeStepPickerLabel } from '../config/requestFormStepRegistry';
import { useRequestScope } from '../contexts/RequestScopeContext';
import {
  overflowScopeMenuId,
  SCOPE_STEP_MENU_BUTTON_ATTR,
  SCOPE_STEP_MENU_PORTAL_ATTR,
  useScopeMenuCoordination,
} from '../contexts/ScopeMenuCoordinationContext';
import { useConfirmationDialog } from '../hooks/useConfirmationDialog';
import { colors } from '../theme/colors';

import type { RequestFormStepId } from '../config/requestFormStepRegistry';

const MENU_MIN_WIDTH = 168;
const MENU_ITEM_HEIGHT = 44;

const scheduleMenuPositionUpdate = (update: () => void) => {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(update);
    return;
  }
  setTimeout(update, 0);
};

function getCreatePortal():
  | ((ui: React.ReactNode, container: Element) => React.ReactNode)
  | null {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    return require('react-dom').createPortal as (
      ui: React.ReactNode,
      container: Element,
    ) => React.ReactNode;
  } catch {
    return null;
  }
}

function resolveMenuPosition(anchorRect: {
  top: number;
  left: number;
  width: number;
  height: number;
}): { top: number; left: number } {
  const screen = Dimensions.get('window');
  const dropdownHeight = MENU_ITEM_HEIGHT + 8;
  const edge = 8;
  let top = anchorRect.top + anchorRect.height + 4;
  let left = anchorRect.left + anchorRect.width - MENU_MIN_WIDTH;
  if (top + dropdownHeight > screen.height - edge) {
    top = anchorRect.top - dropdownHeight - 4;
  }
  if (left + MENU_MIN_WIDTH > screen.width - edge) {
    left = screen.width - MENU_MIN_WIDTH - edge;
  }
  return {
    top: Math.max(edge, top),
    left: Math.max(edge, left),
  };
}

interface ScopeStepOverflowMenuProps {
  stepId: RequestFormStepId;
  stepLabel: string;
  testID?: string;
  stopSidebarActivation?: (event?: { stopPropagation?: () => void }) => void;
}

/** Sidebar row overflow — Remove scope (#997 Chapman mockup). */
export const ScopeStepOverflowMenu: React.FC<ScopeStepOverflowMenuProps> = ({
  stepId,
  stepLabel,
  testID = 'scope-step-overflow-menu',
  stopSidebarActivation,
}) => {
  const scope = useRequestScope();
  const { confirm, confirmationDialog } = useConfirmationDialog();
  const menuId = overflowScopeMenuId(stepId);
  const {
    isOpen: menuOpen,
    open: openMenu,
    close: closeMenu,
  } = useScopeMenuCoordination(menuId);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuAnchorRef = useRef<View>(null);
  const useWebDropdown = Platform.OS === 'web';
  const menuButtonId = `${testID}-${stepId}`;

  const updateMenuPosition = useCallback(() => {
    const anchor = menuAnchorRef.current;
    if (!anchor?.measureInWindow) {
      return;
    }
    anchor.measureInWindow((x, y, width, height) => {
      setMenuPosition(resolveMenuPosition({ top: y, left: x, width, height }));
    });
  }, []);

  const handleRemove = useCallback(async () => {
    closeMenu();
    const label = getScopeStepPickerLabel(stepId);
    const confirmed = await confirm({
      title: 'Remove scope?',
      // #1501: with aggregate save the removal is applied by Save Changes
      // (Cancel keeps the scope), so the copy must not promise it is instant.
      message: featureFlags.aggregateSave
        ? `Removing ${label} will delete all data and attachments in that scope when you save your changes.`
        : `Removing ${label} will delete all data and attachments in that scope. This cannot be undone.`,
      confirmText: 'Remove scope',
      cancelText: 'Cancel',
      confirmButtonStyle: 'danger',
    });
    if (!confirmed) {
      return;
    }
    scope?.removeScope(stepId);
  }, [closeMenu, confirm, scope, stepId]);

  const scopeInteractionDisabled = !scope?.canManageScope;

  const handleMenuPress = useCallback(
    (event?: { stopPropagation?: () => void }) => {
      stopSidebarActivation?.(event);
      if (scopeInteractionDisabled) {
        return;
      }
      if (menuOpen) {
        closeMenu();
        return;
      }
      if (useWebDropdown) {
        openMenu();
        scheduleMenuPositionUpdate(() => {
          updateMenuPosition();
        });
        return;
      }
      updateMenuPosition();
      openMenu();
    },
    [
      closeMenu,
      menuOpen,
      openMenu,
      stopSidebarActivation,
      scopeInteractionDisabled,
      updateMenuPosition,
      useWebDropdown,
    ],
  );

  useEffect(() => {
    if (scopeInteractionDisabled && menuOpen) {
      closeMenu();
    }
  }, [closeMenu, menuOpen, scopeInteractionDisabled]);

  useEffect(() => {
    if (!menuOpen || !useWebDropdown) {
      return undefined;
    }
    updateMenuPosition();
    if (
      typeof window === 'undefined' ||
      typeof window.addEventListener !== 'function'
    ) {
      return undefined;
    }
    const handleReposition = () => {
      updateMenuPosition();
    };
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [menuOpen, updateMenuPosition, useWebDropdown]);

  if (!scope?.showScopeControls) {
    return null;
  }

  if (!scope.removableStepIds.includes(stepId)) {
    return null;
  }

  const menuContent = (
    <Pressable
      style={({ hovered }) => [
        styles.menuItem,
        Platform.OS === 'web' && hovered ? styles.menuItemHovered : null,
      ]}
      onPress={event => {
        event?.stopPropagation?.();
        void handleRemove();
      }}
      testID={`${testID}-remove-${stepId}`}
      accessibilityRole="button"
      accessibilityLabel="Remove scope"
    >
      <View style={styles.menuItemIcon} accessibilityElementsHidden>
        <Trash2 size={16} color={colors.error} />
      </View>
      <Text style={styles.menuItemRemoveText}>Remove scope</Text>
    </Pressable>
  );

  const createPortal = getCreatePortal();
  const webPortaledMenu =
    useWebDropdown &&
    menuOpen &&
    createPortal &&
    typeof document !== 'undefined'
      ? createPortal(
          <View
            {...({
              [SCOPE_STEP_MENU_PORTAL_ATTR]: menuButtonId,
            } as Record<string, string>)}
            testID={`${testID}-panel-${stepId}`}
            onStartShouldSetResponder={() => true}
            style={[
              styles.webPortaledMenu,
              { top: menuPosition.top, left: menuPosition.left },
            ]}
          >
            {menuContent}
          </View>,
          document.body,
        )
      : null;

  return (
    <>
      <View
        ref={menuAnchorRef}
        style={styles.menuAnchor}
        {...(useWebDropdown
          ? ({ [SCOPE_STEP_MENU_BUTTON_ATTR]: menuButtonId } as Record<
              string,
              string
            >)
          : {})}
      >
        <Pressable
          style={[
            styles.menuButton,
            scopeInteractionDisabled && styles.menuButtonDisabled,
          ]}
          onPress={handleMenuPress}
          onPressIn={stopSidebarActivation}
          testID={`${testID}-trigger-${stepId}`}
          accessibilityRole="button"
          accessibilityLabel={`Actions for ${stepLabel}`}
          accessibilityHint={
            scopeInteractionDisabled ? 'Click Edit to remove scope' : undefined
          }
          accessibilityState={{
            expanded: menuOpen,
            disabled: scopeInteractionDisabled,
          }}
          disabled={scopeInteractionDisabled}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <MoreHorizontal
            size={18}
            color={
              scopeInteractionDisabled ? colors.textMuted : colors.textSecondary
            }
          />
        </Pressable>
        {!webPortaledMenu && menuOpen ? (
          <Modal
            visible
            transparent
            animationType="fade"
            onRequestClose={closeMenu}
            statusBarTranslucent
          >
            <Pressable style={styles.menuModalBackdrop} onPress={closeMenu}>
              <View
                onStartShouldSetResponder={() => true}
                style={[
                  styles.menuDropdownModal,
                  { top: menuPosition.top, left: menuPosition.left },
                ]}
                testID={`${testID}-panel-${stepId}`}
              >
                {menuContent}
              </View>
            </Pressable>
          </Modal>
        ) : null}
      </View>
      {webPortaledMenu}
      {confirmationDialog}
    </>
  );
};

const styles = StyleSheet.create({
  menuAnchor: {
    position: 'relative',
    flexShrink: 0,
    zIndex: 20,
    ...Platform.select({
      web: {
        pointerEvents: 'auto' as const,
      },
    }),
  },
  menuButton: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    ...Platform.select({
      web: {
        cursor: 'pointer' as const,
      },
      default: {},
    }),
  },
  menuButtonDisabled: {
    opacity: 0.45,
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as const,
      },
      default: {},
    }),
  },
  menuModalBackdrop: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  menuDropdownModal: {
    position: 'absolute',
    minWidth: MENU_MIN_WIDTH,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.borderLight,
    paddingVertical: 4,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      },
      default: {
        elevation: 8,
      },
    }),
  },
  webPortaledMenu: {
    position: 'fixed',
    minWidth: MENU_MIN_WIDTH,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.borderLight,
    paddingVertical: 4,
    zIndex: 999999,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      },
    }),
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    minHeight: MENU_ITEM_HEIGHT,
    ...Platform.select({
      web: {
        cursor: 'pointer' as const,
      },
    }),
  },
  menuItemHovered: {
    backgroundColor: colors.backgroundSecondary,
  },
  menuItemIcon: {
    width: 16,
    height: 16,
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  menuItemRemoveText: {
    fontSize: 14,
    color: colors.error,
    fontWeight: '500',
  },
});
